/**
 * Investment Pipeline Page — Kanban Board Visualization
 *
 * Columns: Pipeline | Due Diligence | Approved | Disbursed | Active | Matured/Exited
 * Cards: investment summary (investee name, amount, instrument)
 * Click transition buttons to move between stages.
 *
 * Data from /investments/ grouped by status.
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  ChevronLeft,
  DollarSign,
  Briefcase,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import type { Investment, InvestmentStatus } from '@/types';

interface Column {
  key: InvestmentStatus;
  label: string;
  color: string;
  bgColor: string;
}

const COLUMNS: Column[] = [
  { key: 'pipeline', label: 'Pipeline', color: 'text-slate-400', bgColor: 'bg-slate-500/10' },
  { key: 'due_diligence', label: 'Due Diligence', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  { key: 'approved', label: 'Approved', color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
  { key: 'disbursed', label: 'Disbursed', color: 'text-cyan-400', bgColor: 'bg-cyan-500/10' },
  { key: 'active', label: 'Active', color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
  { key: 'matured', label: 'Matured', color: 'text-amber-400', bgColor: 'bg-amber-500/10' },
  { key: 'exited', label: 'Exited', color: 'text-ic-text-tertiary', bgColor: 'bg-slate-500/10' },
];

const STATUS_ORDER: InvestmentStatus[] = ['pipeline', 'due_diligence', 'approved', 'disbursed', 'active', 'matured', 'exited'];

export function InvestmentPipelinePage() {
  const queryClient = useQueryClient();

  const investmentsQuery = useQuery<Investment[]>({
    queryKey: ['ic', 'investments'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/investments/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 30_000,
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: InvestmentStatus }) => {
      const res = await apiClient.patch(`/api/v1/impact-core/investments/${id}/status`, { status });
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'investments'] });
    },
  });

  const investments = investmentsQuery.data ?? [];

  const grouped = useMemo(() => {
    const map: Record<string, Investment[]> = {};
    for (const col of COLUMNS) {
      map[col.key] = [];
    }
    for (const inv of investments) {
      const key = inv.status;
      if (map[key]) {
        map[key].push(inv);
      }
    }
    return map;
  }, [investments]);

  function moveInvestment(inv: Investment, direction: 'forward' | 'backward') {
    const currentIdx = STATUS_ORDER.indexOf(inv.status);
    const newIdx = direction === 'forward' ? currentIdx + 1 : currentIdx - 1;
    if (newIdx < 0 || newIdx >= STATUS_ORDER.length) return;
    const newStatus = STATUS_ORDER[newIdx];
    if (!newStatus) return;
    statusMutation.mutate({ id: inv.id, status: newStatus });
  }

  if (investmentsQuery.isLoading) {
    return (
      <div className="space-y-4">
        <h1 className="text-lg font-semibold text-ic-text-primary">Investment Pipeline</h1>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => (
            <div key={col.key} className="w-64 shrink-0 rounded-lg border border-ic-border bg-ic-card p-3">
              <div className="h-4 w-24 animate-pulse rounded bg-slate-700/50 mb-3" />
              <div className="space-y-2">
                {[1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded bg-slate-700/30" />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">Investment Pipeline</h1>
          <p className="text-xs text-ic-text-tertiary">
            {investments.length} investment{investments.length !== 1 ? 's' : ''} across all stages
          </p>
        </div>
      </div>

      {investmentsQuery.isError ? (
        <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
          <p className="text-xs text-red-400">Failed to load pipeline data.</p>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => {
            const items = grouped[col.key] ?? [];
            const totalAmount = items.reduce((sum, inv) => sum + inv.amount, 0);
            const colIdx = STATUS_ORDER.indexOf(col.key);

            return (
              <div key={col.key} className="w-64 shrink-0 flex flex-col rounded-lg border border-ic-border bg-ic-card">
                {/* Column Header */}
                <div className="flex items-center justify-between border-b border-ic-border px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${col.bgColor.replace('/10', '')}`} style={{ backgroundColor: 'currentColor' }} >
                      <span className={`block h-2 w-2 rounded-full ${col.color}`} />
                    </span>
                    <span className={`text-xs font-medium ${col.color}`}>{col.label}</span>
                  </div>
                  <span className="rounded-full bg-slate-700/50 px-2 py-0.5 text-[10px] font-mono text-ic-text-tertiary">
                    {items.length}
                  </span>
                </div>

                {/* Column Total */}
                {totalAmount > 0 && (
                  <div className="border-b border-ic-border/50 px-3 py-1.5">
                    <span className="font-mono text-[10px] text-ic-text-tertiary">
                      ${(totalAmount / 1_000_000).toFixed(1)}M total
                    </span>
                  </div>
                )}

                {/* Cards */}
                <div className="flex-1 space-y-2 p-2 overflow-y-auto max-h-[calc(100vh-280px)]">
                  {items.length === 0 ? (
                    <div className="flex h-20 items-center justify-center">
                      <p className="text-[10px] text-ic-text-tertiary">No investments</p>
                    </div>
                  ) : (
                    items.map((inv) => (
                      <div
                        key={inv.id}
                        className="rounded-md border border-ic-border/50 bg-ic-surface p-3 hover:border-ic-border transition-colors"
                      >
                        <p className="text-xs font-medium text-ic-text-primary truncate">{inv.investee_name ?? '--'}</p>
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="flex items-center gap-1 text-[10px] text-ic-text-tertiary">
                            <DollarSign className="h-3 w-3" />
                            ${inv.amount.toLocaleString()}
                          </div>
                          {inv.instrument_type && (
                            <div className="flex items-center gap-1 text-[10px] text-ic-text-tertiary">
                              <Briefcase className="h-3 w-3" />
                              {inv.instrument_type}
                            </div>
                          )}
                        </div>
                        {inv.fund_name && (
                          <p className="mt-1 text-[10px] text-ic-text-tertiary truncate">{inv.fund_name}</p>
                        )}

                        {/* Transition buttons */}
                        <div className="mt-2 flex items-center justify-between">
                          {colIdx > 0 ? (
                            <button
                              onClick={() => moveInvestment(inv, 'backward')}
                              disabled={statusMutation.isPending}
                              className="flex items-center gap-0.5 text-[10px] text-ic-text-tertiary hover:text-ic-text-secondary disabled:opacity-50"
                            >
                              <ChevronLeft className="h-3 w-3" />
                              Back
                            </button>
                          ) : <span />}
                          {colIdx < STATUS_ORDER.length - 1 ? (
                            <button
                              onClick={() => moveInvestment(inv, 'forward')}
                              disabled={statusMutation.isPending}
                              className="flex items-center gap-0.5 text-[10px] text-ic-accent hover:text-ic-accent-hover disabled:opacity-50"
                            >
                              Advance
                              <ChevronRight className="h-3 w-3" />
                            </button>
                          ) : <span />}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
